import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Inbox, Camera, Send, Upload, Trash2, Paperclip, Lock, Sparkles, Wand2 } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { matchReceipt, exactPairs } from "../lib/receiptOcr";
import { fmt, dateLabel, today } from "../lib/format";
import { download } from "../lib/reports";
import { PageHeader, Section } from "../components/common";
import { SnapDialog } from "../components/SnapReceipt";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import ReceiptSearch from "../components/ReceiptSearch";
import { SyncNudge } from "../components/DeviceSync";

function AutoAttach() {
  const { state, autoAttachExact } = useStore();
  const { pairs, ambiguous } = exactPairs(state);
  if (!pairs.length) return null;
  const run = () => {
    const r = autoAttachExact();
    toast.success(`Attached ${r.attached} receipt${r.attached === 1 ? "" : "s"} to exact matches${r.ambiguous ? ` · ${r.ambiguous} had more than one possible match — attach those yourself` : ""}`);
  };
  return (
    <div className="rounded-xl bg-income-soft px-3 py-2.5 mb-3 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid="inbox-auto-attach-bar">
      <span className="flex items-center gap-2"><Wand2 className="h-4 w-4 text-income" /><span><b data-testid="inbox-auto-attach-count">{pairs.length}</b> receipt{pairs.length === 1 ? " has" : "s have"} an exact match (same amount, same date).{ambiguous ? ` ${ambiguous} more could match several expenses.` : ""}</span></span>
      <Button size="sm" onClick={run} data-testid="inbox-auto-attach-button"><Paperclip className="h-3.5 w-3.5 mr-1" /> Attach all {pairs.length}</Button>
    </div>
  );
}

function Thumb({ item }) {
  const { getReceipt } = useStore();
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    getReceipt(item).then((b) => setUrl(b ? (u = URL.createObjectURL(b)) : "missing")).catch(() => setUrl("missing"));
    return () => u && URL.revokeObjectURL(u);
  }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (url === "missing") return <div className="h-24 w-20 rounded-lg border bg-secondary grid place-items-center text-[10px] text-center text-muted-foreground p-1 shrink-0" data-testid={`inbox-thumb-missing-${item.id}`}>Photo isn't on this device</div>;
  return url ? <a href={url} target="_blank" rel="noreferrer" className="shrink-0"><img src={url} alt={item.name} className="h-24 w-20 object-cover rounded-lg border bg-white" /></a> : <div className="h-24 w-20 rounded-lg border bg-secondary shrink-0" />;
}

function InboxItem({ item, selected, onSelect }) {
  const { state, editInbox, attachFromInbox, removeFromInbox } = useStore();
  const [all, setAll] = useState(false);
  const matches = matchReceipt(state, item);
  const shown = all ? matches : matches.slice(0, 2);
  const field = (k, props) => <Input value={item[k] || ""} onChange={(e) => editInbox(item.id, { [k]: e.target.value })} className="h-8" data-testid={`inbox-${k}-${item.id}`} {...props} />;
  return (
    <li className="py-4 flex flex-wrap sm:flex-nowrap gap-3" data-testid={`inbox-item-${item.id}`}>
      <Checkbox checked={selected} onCheckedChange={(v) => onSelect(!!v)} className="mt-1" data-testid={`inbox-select-${item.id}`} />
      <Thumb item={item} />
      <div className="basis-full sm:basis-auto order-last sm:order-none flex-1 min-w-0 space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          {field("store", { placeholder: "Store", className: "h-8 sm:col-span-1" })}
          <div className="grid grid-cols-2 gap-2 sm:contents">
            {field("amount", { type: "number", step: "0.01", inputMode: "decimal", placeholder: "Total", className: "h-8 num" })}
            {field("date", { type: "date" })}
          </div>
          {field("note", { placeholder: "Note" })}
        </div>
        {matches.length ? (
          <ul className="space-y-1.5">
            {shown.map(({ tx, exact, learned }) => (
              <li key={tx.id} className="flex items-center gap-2 text-xs rounded-lg bg-secondary/50 px-2 py-1.5" data-testid={`inbox-match-${item.id}-${tx.id}`}>
                <span className="flex-1 min-w-0">
                  <span className="block font-medium truncate">{learned && <Sparkles className="inline h-3 w-3 text-primary mr-1" />}{tx.description}</span>
                  <span className="block text-muted-foreground whitespace-nowrap">{dateLabel(tx.date)} · <span className="num">{fmt(Math.abs(tx.amount))}</span>{exact && <span className="text-income"> · exact</span>}</span>
                </span>
                <Button size="sm" className="h-7" aria-label={`Attach to ${tx.description}`} onClick={() => { attachFromInbox(item.id, tx.id); toast.success(`Attached to ${tx.description}`); }} data-testid={`inbox-attach-${item.id}-${tx.id}`}><Paperclip className="h-3 w-3 sm:mr-1" /><span className="hidden sm:inline">Attach</span></Button>
              </li>
            ))}
            {matches.length > 2 && <button className="text-xs text-primary underline" onClick={() => setAll(!all)} data-testid={`inbox-more-${item.id}`}>{all ? "Fewer" : `${matches.length - 2} more matches`}</button>}
          </ul>
        ) : <p className="text-xs text-muted-foreground" data-testid={`inbox-nomatch-${item.id}`}>{state.transactions.length ? "No matching expense yet — import that month's statement or fix the total/date." : "No transactions on this device — send this receipt to your computer."}</p>}
      </div>
      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 ml-auto sm:ml-0" onClick={() => window.confirm("Delete this receipt photo?") && removeFromInbox([item.id])} data-testid={`inbox-delete-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
    </li>
  );
}

function SendBar({ ids, onSent }) {
  const { exportInboxBundle, removeFromInbox } = useStore();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!ids.length) return toast.error("Select receipts to send");
    if (!pw && !window.confirm("Send without a password? Anyone who gets the file (e.g. by email) could see the photos.")) return;
    setBusy(true);
    try {
      const { text, count } = await exportInboxBundle(ids, pw);
      const name = `receipts-${today()}-${count}.json`;
      const file = new File([text], name, { type: "application/json" });
      let shared = false;
      if (navigator.canShare?.({ files: [file] })) {
        try { await navigator.share({ files: [file], title: `${count} receipt(s)` }); shared = true; } catch (e) { if (e.name === "AbortError") { setBusy(false); return; } }
      }
      if (!shared) download(text, name, "application/json");
      toast.success(shared ? "Sent! Import the file on your computer: Receipt inbox → Import from phone." : "Bundle saved. AirDrop or email it, then import it on your computer.");
      if (window.confirm(`Remove the ${count} sent receipt(s) from this phone's inbox?`)) await removeFromInbox(ids);
      onSent();
    } catch (e) {
      console.error(e);
      toast.error("Couldn't create the bundle");
    }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="inbox-send-bar">
      <Input type="password" placeholder="Password (recommended)" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full sm:max-w-[240px] h-9" data-testid="inbox-send-password-input" />
      <Button onClick={send} disabled={busy || !ids.length} data-testid="inbox-send-button"><Send className="h-4 w-4 mr-1" /> {busy ? "Preparing…" : `Send ${ids.length || ""} to computer`}</Button>
    </div>
  );
}

function ImportBar() {
  const { importInboxBundle } = useStore();
  const [pw, setPw] = useState("");
  const ref = useRef(null);
  const run = async (file) => {
    try {
      const { added, skipped } = await importInboxBundle(await file.text(), pw);
      toast.success(`${added} receipt(s) added to your inbox${skipped ? ` (${skipped} already here)` : ""}`);
    } catch (e) {
      toast.error(e.message || "Couldn't import that file");
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="inbox-import-bar">
      <Input type="password" placeholder="Bundle password (if set)" value={pw} onChange={(e) => setPw(e.target.value)} className="max-w-[220px] h-9" data-testid="inbox-import-password-input" />
      <Button variant="outline" onClick={() => ref.current?.click()} data-testid="inbox-import-button"><Upload className="h-4 w-4 mr-1" /> Import from phone</Button>
      <input ref={ref} type="file" accept="application/json,.json" className="hidden" onChange={(e) => { e.target.files?.[0] && run(e.target.files[0]); e.target.value = ""; }} data-testid="inbox-import-file-input" />
    </div>
  );
}

export default function ReceiptInbox() {
  const { state } = useStore();
  const items = [...(state.receiptInbox || [])].sort((a, b) => b.addedAt.localeCompare(a.addedAt));
  const [sel, setSel] = useState([]);
  const [snap, setSnap] = useState(false);
  const ids = sel.filter((id) => items.some((i) => i.id === id));
  return (
    <div data-testid="receipt-inbox-page">
      <PageHeader title="Receipt inbox" subtitle="Snap receipts on your phone, then attach them to transactions here — or send them to your computer by AirDrop or email.">
        <Button onClick={() => setSnap(true)} className="rounded-full" data-testid="inbox-snap-button"><Camera className="h-4 w-4 mr-1" /> Snap receipt</Button>
      </PageHeader>
      <SyncNudge className="mb-5" />
      <Tabs defaultValue="inbox">
      <TabsList className="mb-5">
        <TabsTrigger value="inbox" data-testid="inbox-tab-inbox">Inbox ({items.length})</TabsTrigger>
        <TabsTrigger value="search" data-testid="inbox-tab-search">Search all receipts</TabsTrigger>
      </TabsList>
      <TabsContent value="search"><Section title="Search receipts" testid="receipt-search-section"><ReceiptSearch /></Section></TabsContent>
      <TabsContent value="inbox">
      <div className="space-y-6">
        <Section title="How phone → computer works" action={<Lock className="h-5 w-5 text-muted-foreground" />} testid="inbox-howto">
          <ol className="grid md:grid-cols-3 gap-3 text-sm">
            <li className="rounded-xl border p-3"><b className="text-primary">1. On your phone</b><br />Tap the camera button, snap each receipt, and save it to the inbox.</li>
            <li className="rounded-xl border p-3"><b className="text-primary">2. Send</b><br />Select the receipts, add a password, then tap <b>Send to computer</b> and choose AirDrop or Mail.</li>
            <li className="rounded-xl border p-3"><b className="text-primary">3. On your computer</b><br />Open this page, enter the password, and tap <b>Import from phone</b>. Then attach each receipt to its expense.</li>
          </ol>
          <p className="text-xs text-muted-foreground mt-3">The file goes straight between your devices. It never passes through this app's servers, and with a password it's encrypted (AES-256).</p>
        </Section>
        <Section title={`Waiting to be matched (${items.length})`} action={<Inbox className="h-5 w-5 text-muted-foreground" />} testid="inbox-list-section">
          <div className="flex flex-wrap justify-between gap-3 mb-2">
            <ImportBar />
            {items.length > 0 && <SendBar ids={ids} onSent={() => setSel([])} />}
          </div>
          <AutoAttach />
          {items.length > 0 && <label className="flex items-center gap-2 text-xs text-muted-foreground mt-3"><Checkbox checked={ids.length === items.length} onCheckedChange={(v) => setSel(v ? items.map((i) => i.id) : [])} data-testid="inbox-select-all" /> Select all</label>}
          {items.length ? (
            <ul className="divide-y">{items.map((it) => <InboxItem key={it.id} item={it} selected={ids.includes(it.id)} onSelect={(v) => setSel((s) => (v ? [...s, it.id] : s.filter((x) => x !== it.id)))} />)}</ul>
          ) : <p className="text-sm text-muted-foreground mt-3" data-testid="inbox-empty">Your inbox is empty. Snap a receipt or import a bundle from your phone.</p>}
        </Section>
      </div>
      </TabsContent>
      </Tabs>
      {snap && <SnapDialog onClose={() => setSnap(false)} />}
    </div>
  );
}
